<template>
    <div class="flex flex-col gap-4">
        <p v-if="error" class="text-sm text-rose-500">
            Não foi possível carregar o runoff da reserva.
        </p>

        <div v-else-if="loading" class="flex flex-col gap-3">
            <div
                class="h-5 w-2/3 rounded-sm bg-gray-100 dark:bg-gray-700"
            ></div>
            <div
                class="h-4 w-1/2 rounded-sm bg-gray-100 dark:bg-gray-700"
            ></div>
        </div>

        <template v-else-if="runoff">
            <!-- Veredito: a decisão do operador em uma frase -->
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2">
                    <span
                        v-if="insight.badge"
                        class="rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]"
                        :class="statusChip(insight.status)"
                    >
                        {{ insight.badge }}
                    </span>
                </div>
                <p
                    class="text-sm font-medium leading-relaxed text-gray-900 dark:text-white"
                >
                    <highlighted-text :text="insight.headline" />
                </p>
            </div>

            <!-- Só os números que sustentam o veredito -->
            <div
                v-if="runoff.reserve.schedule.length"
                class="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 dark:border-gray-700 dark:bg-gray-700 sm:grid-cols-3"
            >
                <article
                    v-for="stat in stats"
                    :key="stat.label"
                    class="bg-white px-5 py-4 dark:bg-gray-800"
                >
                    <h3
                        class="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                    >
                        {{ stat.label }}
                    </h3>
                    <p
                        class="mt-2 font-mono text-base font-bold tabular-nums tracking-tight"
                        :class="
                            stat.tone === 'danger'
                                ? 'text-rose-600 dark:text-rose-400'
                                : 'text-gray-900 dark:text-white'
                        "
                    >
                        {{ stat.value }}
                    </p>
                    <p
                        class="mt-1 text-[11px] text-gray-400 dark:text-gray-500"
                    >
                        {{ stat.hint }}
                    </p>
                </article>
            </div>

            <!-- Reserva x exposição no tempo: o cruzamento é o descoberto -->
            <section v-if="chartSeries.length" class="flex flex-col gap-2">
                <h3
                    class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
                >
                    Reserva vs exposição no tempo
                </h3>
                <line-chart :series="chartSeries" />
            </section>

            <!-- Passado explica o futuro, mas em outro domínio de tempo:
                 as coortes são datas de VENDA, a agenda é data de LIBERAÇÃO.
                 Misturar os dois no mesmo eixo X induz leitura errada. -->
            <section v-if="cohortRateSeries.length" class="flex flex-col gap-2">
                <h3
                    class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
                >
                    Taxa de CB por coorte de venda
                </h3>
                <line-chart
                    :series="cohortRateSeries"
                    :percent="true"
                    :height="180"
                />
            </section>

            <!-- Checkpoints: onde a decisão vira ação -->
            <section v-if="openCheckpoints.length" class="flex flex-col gap-2">
                <h3
                    class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
                >
                    Checkpoints de decisão
                </h3>
                <div
                    v-for="checkpoint in openCheckpoints"
                    :key="checkpoint.id"
                    class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                >
                    <div class="flex flex-wrap items-center gap-2">
                        <span
                            class="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        >
                            {{ triggerLabel(checkpoint.trigger) }}
                        </span>
                        <span
                            class="font-mono text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
                        >
                            decidir até {{ dateLabel(checkpoint.due_date) }}
                        </span>
                        <span
                            v-if="checkpoint.status === 'auto_resolved'"
                            class="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                            :class="statusChip('coberto')"
                        >
                            critério passou
                        </span>
                    </div>

                    <!-- Os números da decisão: o que libera, o que fica e
                         o que ainda pode entrar contra esse resto -->
                    <div
                        v-if="checkpointFacts(checkpoint).length"
                        class="flex flex-wrap gap-x-6 gap-y-2"
                    >
                        <div
                            v-for="fact in checkpointFacts(checkpoint)"
                            :key="fact.label"
                            class="flex flex-col"
                        >
                            <span
                                class="text-[9px] font-medium uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500"
                            >
                                {{ fact.label }}
                            </span>
                            <span
                                class="font-mono text-xs font-semibold tabular-nums"
                                :class="
                                    fact.tone === 'danger'
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : 'text-gray-900 dark:text-white'
                                "
                            >
                                {{ fact.value }}
                            </span>
                        </div>
                    </div>

                    <p class="text-xs text-gray-600 dark:text-gray-300">
                        {{
                            checkpoint.recommendation?.reason ??
                            "Sem recomendação registrada."
                        }}
                    </p>

                    <form
                        v-if="decidingId === checkpoint.id"
                        class="flex flex-col gap-3"
                        @submit.prevent="submitDecision(checkpoint)"
                    >
                        <div class="flex flex-wrap items-center gap-4">
                            <label
                                v-for="option in decisionOptions"
                                :key="option.value"
                                class="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200"
                            >
                                <input
                                    v-model="decisionForm.decision"
                                    type="radio"
                                    :value="option.value"
                                />
                                {{ option.label }}
                            </label>
                            <select
                                v-if="decisionForm.decision === 'estender'"
                                v-model.number="decisionForm.extend_days"
                                class="rounded-md border-gray-300 bg-white py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                            >
                                <option
                                    v-for="days in extendOptions"
                                    :key="days"
                                    :value="days"
                                >
                                    +{{ days }} dias
                                </option>
                            </select>
                        </div>
                        <textarea
                            v-model="decisionForm.note"
                            rows="2"
                            required
                            minlength="10"
                            placeholder="Nota obrigatória: por que essa decisão?"
                            class="rounded-md border-gray-300 bg-white text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                        ></textarea>
                        <p v-if="decisionError" class="text-xs text-rose-500">
                            {{ decisionError }}
                        </p>
                        <div class="flex items-center gap-3">
                            <button
                                type="submit"
                                :disabled="deciding"
                                class="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                            >
                                Confirmar decisão
                            </button>
                            <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                @click="decidingId = null"
                            >
                                Cancelar
                            </button>
                        </div>
                    </form>
                    <button
                        v-else
                        type="button"
                        class="self-start rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        @click="openDecision(checkpoint)"
                    >
                        Decidir
                    </button>
                </div>
            </section>

            <button
                type="button"
                class="self-start text-xs font-medium text-gray-500 underline decoration-dotted underline-offset-4 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                @click="showDetails = !showDetails"
            >
                {{
                    showDetails ? "Ocultar detalhes" : "Ver detalhes do cálculo"
                }}
            </button>

            <template v-if="showDetails">
                <!-- Agenda de liberação com cobertura por data -->
                <section
                    v-if="runoff.reserve.schedule.length"
                    class="flex flex-col gap-2"
                >
                    <h3
                        class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
                    >
                        Agenda de liberação
                    </h3>
                    <div
                        class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                        <table class="min-w-full text-sm">
                            <thead
                                class="bg-gray-50 text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:bg-gray-900/40 dark:text-gray-400"
                            >
                                <tr>
                                    <th class="px-4 py-2 font-medium">
                                        Libera em
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Valor
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Reserva restante
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Runoff pessimista após
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Cobertura
                                    </th>
                                    <th class="px-4 py-2 font-medium">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody
                                class="divide-y divide-gray-100 bg-white dark:divide-gray-700/60 dark:bg-gray-800"
                            >
                                <tr
                                    v-for="entry in runoff.reserve.schedule"
                                    :key="entry.date"
                                >
                                    <td
                                        class="px-4 py-2 font-mono tabular-nums text-gray-900 dark:text-white"
                                    >
                                        {{ dateLabel(entry.date) }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-900 dark:text-white"
                                    >
                                        {{ money(entry.amount) }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{
                                            money(entry.remaining_reserve_after)
                                        }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{
                                            money(
                                                entry.runoff_pessimistic_after,
                                            )
                                        }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{
                                            entry.coverage === null
                                                ? "—"
                                                : entry.coverage + "x"
                                        }}
                                    </td>
                                    <td class="px-4 py-2">
                                        <span
                                            class="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                                            :class="statusChip(entry.status)"
                                        >
                                            {{ entry.status }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- Coortes: competência revela a safra, maturidade diz o que falta -->
                <section
                    v-if="cohortsWithSales.length"
                    class="flex flex-col gap-2"
                >
                    <h3
                        class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
                    >
                        Coortes de venda (competência)
                    </h3>
                    <div
                        class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                        <table class="min-w-full text-sm">
                            <thead
                                class="bg-gray-50 text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:bg-gray-900/40 dark:text-gray-400"
                            >
                                <tr>
                                    <th class="px-4 py-2 font-medium">
                                        Coorte
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Vendas
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        CB observado
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Taxa
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Maturidade
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Falta (central)
                                    </th>
                                    <th
                                        class="px-4 py-2 text-right font-medium"
                                    >
                                        Falta (pess.)
                                    </th>
                                </tr>
                            </thead>
                            <tbody
                                class="divide-y divide-gray-100 bg-white dark:divide-gray-700/60 dark:bg-gray-800"
                            >
                                <tr
                                    v-for="cohort in cohortsWithSales"
                                    :key="cohort.cohort"
                                >
                                    <td
                                        class="px-4 py-2 font-mono tabular-nums text-gray-900 dark:text-white"
                                    >
                                        {{ cohort.cohort }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{ money(cohort.sales_amount) }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{ money(cohort.cb_amount) }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums"
                                        :class="
                                            cohort.rate_pct >= 3
                                                ? 'text-rose-500'
                                                : 'text-gray-900 dark:text-white'
                                        "
                                    >
                                        {{ cohort.rate_pct.toFixed(2) }}%
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{ cohort.maturity_pct.toFixed(0) }}%
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-900 dark:text-white"
                                    >
                                        {{ money(cohort.remaining_central) }}
                                    </td>
                                    <td
                                        class="px-4 py-2 text-right font-mono tabular-nums text-gray-500 dark:text-gray-400"
                                    >
                                        {{
                                            money(cohort.remaining_pessimistic)
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <p
                    class="font-mono text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
                >
                    apurado {{ computedAtLabel }} · já liberado
                    {{ money(runoff.reserve.released_total) }} · curva
                    {{ curveSourceLabel }} · taxa pessimista
                    {{ runoff.pessimistic_rate_pct.toFixed(2) }}% · régua
                    canônica (comissão, cartão e wallets) · central =
                    chain-ladder, pessimista = pior safra madura sobre o
                    não-maturado
                </p>
            </template>
        </template>
    </div>
</template>

<script>
import LineChart from "../../analytics/charts/LineChart.vue";
import HighlightedText from "../../../views/monitoring/HighlightedText.vue";

export default {
    components: { LineChart, HighlightedText },

    props: {
        user: { required: true },
    },

    data() {
        return {
            loading: true,
            error: false,
            runoff: null,
            showDetails: false,
            checkpoints: [],
            extendOptions: [15, 30, 45, 60],
            decidingId: null,
            deciding: false,
            decisionError: null,
            decisionForm: { decision: "liberar", extend_days: 30, note: "" },
        };
    },

    computed: {
        insight() {
            return this.buildInsight(this.runoff);
        },

        stats() {
            const reserve = this.runoff.reserve;
            const totals = this.runoff.runoff;
            const schedule = reserve.schedule;
            const critical = this.insight.critical;

            return [
                {
                    label: "Reserva pendente",
                    value: this.money(reserve.pending_total),
                    hint: `${schedule.length} ${schedule.length === 1 ? "parcela" : "parcelas"} até ${this.dateLabel(schedule[schedule.length - 1].date)}`,
                },
                {
                    label: "Ainda pode entrar",
                    value: `${this.money(totals.total_central)} a ${this.money(totals.total_pessimistic)}`,
                    hint: "chargeback + reembolso, central a pessimista",
                },
                {
                    label: "Data crítica",
                    value: critical ? this.dateLabel(critical.date) : "nenhuma",
                    hint: critical
                        ? `parcela de ${this.money(critical.amount)}`
                        : "todas as liberações cobertas",
                    ...(critical && { tone: "danger" }),
                },
            ];
        },

        cohortsWithSales() {
            return this.runoff.cohorts.filter(
                (cohort) => cohort.sales_amount > 0,
            );
        },

        openCheckpoints() {
            return this.checkpoints.filter((checkpoint) =>
                ["pending", "auto_resolved"].includes(checkpoint.status),
            );
        },

        /**
         * Reserva restante (degraus) contra exposicao que ainda pode entrar
         * (derrete com a maturacao), de hoje ate a ultima liberacao. Onde a
         * reserva cruza abaixo da pessimista esta o descoberto.
         */
        chartSeries() {
            const schedule = this.runoff.reserve.schedule;

            if (!schedule.length) {
                return [];
            }

            // Meia-noite LOCAL: eixo e tooltip mostram o dia certo em BRT.
            const ts = (date) => new Date(`${date}T00:00:00`).getTime();
            const computedAt = new Date(this.runoff.computed_at);
            const todayTs = new Date(
                computedAt.getFullYear(),
                computedAt.getMonth(),
                computedAt.getDate(),
            ).getTime();
            const reais = (cents) => Math.round(cents) / 100;

            const point = (field, todayValue) => [
                [todayTs, reais(todayValue)],
                ...schedule.map((entry) => [
                    ts(entry.date),
                    reais(entry[field]),
                ]),
            ];

            return [
                {
                    name: "Reserva retida",
                    data: point(
                        "remaining_reserve_after",
                        this.runoff.reserve.pending_total,
                    ),
                    step: "left",
                    color: "#10b981",
                    lineWidth: 2,
                },
                {
                    name: "Exposição pessimista",
                    data: point(
                        "runoff_pessimistic_after",
                        this.runoff.runoff.total_pessimistic,
                    ),
                    color: "#f43f5e",
                    lineWidth: 2,
                },
                {
                    name: "Exposição central",
                    data: point(
                        "runoff_central_after",
                        this.runoff.runoff.total_central,
                    ),
                    color: "#9ca3af",
                    dashStyle: "ShortDash",
                    lineWidth: 1,
                },
            ];
        },

        /**
         * Passado explica o futuro: competencia por coorte de VENDA, em
         * grafico proprio. Fica fora do grafico da agenda porque os eixos
         * de tempo sao outros (data de venda vs data de liberacao).
         */
        cohortRateSeries() {
            if (this.cohortsWithSales.length < 2) {
                return [];
            }

            return [
                {
                    name: "Taxa de CB por coorte (%)",
                    data: this.cohortsWithSales.map((cohort) => [
                        new Date(`${cohort.cohort}-15T00:00:00`).getTime(),
                        cohort.rate_pct,
                    ]),
                    color: "#ef4444",
                    dashStyle: "Dot",
                    lineWidth: 1.5,
                    marker: { enabled: true, radius: 2.5 },
                },
            ];
        },

        decisionOptions() {
            return [
                { value: "liberar", label: "Liberar" },
                { value: "estender", label: "Estender" },
                { value: "travar_saque", label: "Travar saque" },
            ];
        },

        computedAtLabel() {
            return new Date(this.runoff.computed_at).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        curveSourceLabel() {
            return this.runoff.curve.source === "own"
                ? "própria do seller"
                : "global (fallback)";
        },
    },

    watch: {
        user() {
            this.fetch();
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        userId() {
            return this.$route?.params?.user_id ?? this.user.id;
        },

        fetch() {
            this.loading = true;
            this.error = false;
            this.fetchCheckpoints();

            axios
                .get(`/api/financial/reserve-runoff/${this.userId()}`)
                .then(({ data }) => {
                    this.runoff = data;
                    // O resumo do card-header sai daqui: a leitura de status
                    // (descoberto > apertado > coberto) vive num lugar so.
                    this.$emit("data-loaded", {
                        ...data,
                        ui_summary: {
                            priority: this.insight.priority,
                            badge: this.insight.badge,
                            summary: this.insight.summary,
                        },
                    });
                })
                .catch(() => {
                    this.error = true;
                    this.$emit("data-loaded", null);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * Traduz o payload em uma decisão: qual parcela segurar (se alguma)
         * e por quê. Headline alimenta o corpo do card; summary, o header.
         */
        buildInsight(runoff) {
            const pending = runoff.reserve.pending_total;
            const pessimistic = runoff.runoff.total_pessimistic;
            const schedule = runoff.reserve.schedule;

            if (pending === 0 && pessimistic === 0) {
                return {
                    priority: "normal",
                    status: null,
                    badge: null,
                    critical: null,
                    summary: "Sem reserva pendente e sem runoff aberto",
                    headline:
                        "Sem reserva pendente de liberação e sem chargeback ou reembolso em aberto.",
                };
            }

            if (schedule.length === 0) {
                return {
                    priority: "normal",
                    status: null,
                    badge: null,
                    critical: null,
                    summary: `Sem agenda, runoff aberto ${this.money(pessimistic)}`,
                    headline: `Sem parcelas de reserva a liberar, mas ainda podem entrar até ${this.money(pessimistic)} de chargeback e reembolso.`,
                };
            }

            const uncovered = schedule.find(
                (entry) => entry.status === "descoberto",
            );

            if (uncovered) {
                const remaining = uncovered.remaining_reserve_after;
                const tail =
                    remaining > 0
                        ? `depois dela sobram ${this.money(remaining)} de reserva contra até ${this.money(uncovered.runoff_pessimistic_after)} ainda por entrar.`
                        : `ela zera a reserva com até ${this.money(uncovered.runoff_pessimistic_after)} de chargeback e reembolso ainda por entrar.`;

                return {
                    priority: "danger",
                    status: "descoberto",
                    badge: "Descoberto",
                    critical: uncovered,
                    summary: `Segurar a parcela de ${this.dateLabel(uncovered.date)} (${this.money(uncovered.amount)})`,
                    headline: `Segurar a liberação de ${this.dateLabel(uncovered.date)} (${this.money(uncovered.amount)}): ${tail}`,
                };
            }

            const tight = schedule.find((entry) => entry.status === "apertado");

            if (tight) {
                return {
                    priority: "warning",
                    status: "apertado",
                    badge: "Apertado",
                    critical: tight,
                    summary: `Acompanhar a parcela de ${this.dateLabel(tight.date)} (cobertura ${tight.coverage}x)`,
                    headline: `Acompanhar a liberação de ${this.dateLabel(tight.date)} (${this.money(tight.amount)}): a reserva restante cobre só ${tight.coverage}x o que ainda pode entrar.`,
                };
            }

            return {
                priority: "normal",
                status: "coberto",
                badge: "Coberto",
                critical: null,
                summary: `${this.money(pending)} pendente, todas as liberações cobertas`,
                headline: `Pode deixar a agenda correr: a reserva cobre o que ainda pode entrar em todas as ${schedule.length} datas de liberação.`,
            };
        },

        fetchCheckpoints() {
            axios
                .get(
                    `/api/financial/users/${this.userId()}/reserve-checkpoints`,
                )
                .then(({ data }) => {
                    this.checkpoints = data.checkpoints;
                    // Opcoes de extensao vem do backend: mesma fonte que a
                    // validacao usa, sem lista paralela no front.
                    this.extendOptions =
                        data.extend_days_options ?? this.extendOptions;
                })
                .catch(() => {
                    this.checkpoints = [];
                });
        },

        openDecision(checkpoint) {
            this.decidingId = checkpoint.id;
            this.decisionError = null;
            this.decisionForm = {
                decision:
                    checkpoint.recommendation?.suggested === "estender"
                        ? "estender"
                        : "liberar",
                extend_days: 30,
                note: "",
            };
        },

        submitDecision(checkpoint) {
            this.deciding = true;
            this.decisionError = null;

            const payload = {
                decision: this.decisionForm.decision,
                note: this.decisionForm.note,
            };

            if (this.decisionForm.decision === "estender") {
                payload.extend_days = this.decisionForm.extend_days;
            }

            axios
                .post(
                    `/api/financial/reserve-checkpoints/${checkpoint.id}/decision`,
                    payload,
                )
                .then(() => {
                    this.decidingId = null;

                    // So estender muda a agenda; nas demais decisoes o runoff
                    // continua igual e basta recarregar os checkpoints.
                    if (payload.decision === "estender") {
                        this.fetch();
                    } else {
                        this.fetchCheckpoints();
                    }
                })
                .catch((error) => {
                    this.decisionError =
                        error.response?.data?.message ??
                        "Não foi possível registrar a decisão.";
                })
                .finally(() => {
                    this.deciding = false;
                });
        },

        /**
         * Numeros que sustentam a decisao do checkpoint. Checkpoint de
         * parcela cruza a recommendation com a linha da agenda na mesma
         * data; checkpoint de conta (churn, venda sem retencao) nao governa
         * parcela e mostra o total pendente como contexto.
         */
        checkpointFacts(checkpoint) {
            const recommendation = checkpoint.recommendation ?? {};
            const reserve = this.runoff?.reserve;

            if (!recommendation.release_date) {
                if (!reserve?.pending_total) {
                    return [];
                }

                return [
                    {
                        label: "Reserva pendente",
                        value: this.money(reserve.pending_total),
                    },
                ];
            }

            const facts = [
                {
                    label: "Libera",
                    value:
                        recommendation.amount != null
                            ? `${this.money(recommendation.amount)} em ${this.dateLabel(recommendation.release_date)}`
                            : this.dateLabel(recommendation.release_date),
                },
            ];

            const entry = reserve?.schedule?.find(
                (item) => item.date === recommendation.release_date,
            );

            if (entry) {
                facts.push(
                    {
                        label: "Reserva após",
                        value: this.money(entry.remaining_reserve_after),
                    },
                    {
                        label: "Ainda pode entrar",
                        value: this.money(entry.runoff_pessimistic_after),
                    },
                    {
                        label: "Cobertura",
                        value:
                            entry.coverage === null
                                ? "—"
                                : `${entry.coverage}x`,
                        ...(entry.status === "descoberto" && {
                            tone: "danger",
                        }),
                    },
                );
            }

            return facts;
        },

        triggerLabel(trigger) {
            return (
                {
                    parcela_grande: "Parcela grande",
                    extensao_manual: "Extensão manual",
                    churn_detectado: "Churn",
                    cauda_final: "Cauda final",
                    venda_sem_retencao: "Venda sem retenção",
                    manual: "Manual",
                }[trigger] ?? trigger
            );
        },

        money(cents) {
            return (cents / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },

        dateLabel(value) {
            // "YYYY-MM-DD" puro e lido pelo JS como meia-noite UTC e, em
            // BRT, voltaria um dia; ancora no fuso local antes de formatar.
            const normalized =
                typeof value === "string" && value.length === 10
                    ? `${value}T00:00:00`
                    : value;

            return new Date(normalized).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
            });
        },

        statusChip(status) {
            return {
                coberto:
                    "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
                apertado:
                    "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
                descoberto:
                    "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
            }[status];
        },
    },
};
</script>
